import { useCallback, useMemo } from "react"
import { useCore } from "@app/hooks/useCore"
import { isTrackingAllowed } from "@app/utils/tracking"

export const useKlaviyo = () => {
  const {
    helpers: { isDev, isBrowser },
  } = useCore()

  const _learnq = useMemo(() => (isBrowser && window._learnq ? window._learnq : []), [isBrowser])

  const cleanData = (data: any) => {
    try {
      const clean = JSON.parse(
        JSON.stringify(data, (k, v) =>
          ["id", "internal", "parent", "metadata"].includes(k) || k.startsWith("_")
            ? undefined
            : ["image", "thumbnail"].includes(k)
            ? { alt: v?.alt, url: v?.asset?.url }
            : ["handle"].includes(k)
            ? v?.current
            : ["date", "dateCreated", "dateUpdated", "publishedAt"].includes(k)
            ? `${new Date(v).toISOString()}`
            : ["content", "subcontent", "additionalContent", "description"].includes(k) && Array.isArray(v)
            ? v?.map(({ children }) => children?.[0]?.text)?.join("<br/>")
            : Array.isArray(v) && !v?.length
            ? undefined
            : v?.constructor === Object && Object.entries(v)?.length === 0
            ? undefined
            : typeof v === "boolean"
            ? v
            : v || undefined
        )
      )
      return clean
    } catch (err) {
      console.error("Error parsing klaviyo event data")
    }
    return
  }

  const identify = useCallback(
    customer => {
      if (!isTrackingAllowed()) return
      if (isDev) console.info(`[KLAVIYO] identify fired.`)
      _learnq.push([
        "identify",
        {
          $email: `${customer?.email}`,
          $first_name: `${customer?.firstName || ""}`,
          $last_name: `${customer?.lastName || ""}`,
          // Custom Properties...
        },
      ])
    },
    [_learnq, isDev]
  )

  const track = useCallback(
    (type, item) => {
      if (!isTrackingAllowed()) return
      if (isDev) console.info(`[KLAVIYO] track(${type}) fired.`)
      _learnq.push(["track", type, { ...cleanData(item) }])
    },
    [_learnq, isDev]
  )

  return { identify, track }
}
